import { useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import type { FaqItem } from '@/content/faq'
import { Stagger } from '@/components/motion/Reveal'
import { cn } from '@/lib/cn'

/**
 * Built on <details>/<summary> so it stays keyboard- and screen-reader-correct
 * without ARIA, and still works with JavaScript disabled.
 *
 * `<details>` gives no transition of its own, so the element is held open for
 * the duration of a close so the collapse can be animated, using a
 * `grid-template-rows` 1fr → 0fr transition rather than a guessed max-height.
 */
function AccordionItem({ item }: { item: FaqItem }) {
  const [open, setOpen] = useState(false)
  const [closing, setClosing] = useState(false)
  const timer = useRef<number>()

  useEffect(() => () => window.clearTimeout(timer.current), [])

  /**
   * The summary click is intercepted rather than letting `<details>` toggle
   * itself: closing has to be held open for the length of the animation, and
   * writing `open` back onto the element would re-enter its own toggle event.
   * Enter and Space both arrive here as clicks, so keyboard use is covered.
   */
  const onSummaryClick = (event: React.MouseEvent<HTMLElement>) => {
    event.preventDefault()
    window.clearTimeout(timer.current)

    if (!open) {
      setClosing(false)
      setOpen(true)
      return
    }

    setOpen(false)
    setClosing(true)
    timer.current = window.setTimeout(() => setClosing(false), 320)
  }

  return (
    <details open={open || closing} className="group border-b border-line last:border-b-0">
      <summary
        onClick={onSummaryClick}
        className={cn(
          'flex cursor-pointer list-none items-start justify-between gap-4 py-5 text-[15px] font-bold leading-snug transition-colors duration-300 [&::-webkit-details-marker]:hidden',
          open ? 'text-primary' : 'text-ink hover:text-primary',
        )}
      >
        <span className="text-pretty">{item.question}</span>
        <span
          aria-hidden="true"
          className={cn(
            'mt-0.5 flex h-7 w-7 flex-none items-center justify-center rounded-full border transition-all duration-300 ease-brand',
            open
              ? 'rotate-45 border-primary/40 bg-primary/10 text-primary'
              : 'border-line text-muted group-hover:border-primary/30 group-hover:text-primary',
          )}
        >
          {/* A plus that rotates into a minus reads better than a chevron here. */}
          <svg
            aria-hidden="true"
            viewBox="0 0 16 16"
            className="h-3.5 w-3.5"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
          >
            <path d="M8 3.5v9M3.5 8h9" strokeLinecap="round" />
          </svg>
        </span>
      </summary>

      <div
        className="grid transition-[grid-template-rows,opacity] duration-300 ease-brand motion-reduce:transition-none"
        style={{ gridTemplateRows: open ? '1fr' : '0fr', opacity: open ? 1 : 0 }}
      >
        <div className="overflow-hidden">
          <div className="pb-6 pr-10">
            <p className="max-w-measure text-pretty text-[14.5px] leading-[1.75] text-muted">
              {item.answer}
            </p>
            {item.link ? (
              <p className="mt-3">
                <Link
                  to={item.link.to}
                  className="link-underline text-[13.5px] font-semibold text-primary hover:text-primary-hover"
                >
                  {item.link.label}
                </Link>
              </p>
            ) : null}
          </div>
        </div>
      </div>
    </details>
  )
}

export function Accordion({ items }: { items: FaqItem[] }) {
  return (
    <Stagger
      variant="up"
      step={70}
      className="card-spot rounded-card border border-line bg-white/85 px-6 shadow-card backdrop-blur sm:px-7"
    >
      {items.map((item) => (
        <AccordionItem key={item.id} item={item} />
      ))}
    </Stagger>
  )
}
